import { forwardRef, type ReactElement, type ReactNode } from "react"
import {
  HeartIcon,
  HomeIcon,
  TextSearchIcon,
  ShoppingBagIcon,
  UserRoundIcon,
  type LucideIcon,
} from "lucide-react"
import { Link } from "react-router"

import { Badge } from "@/components/ui/shadcn/badge"
import { Button, buttonVariants } from "@/components/ui/shadcn/button"
import { cn } from "@/lib/utils"
import type { MobileStoreSection } from "./mobileStoreNavigationTypes"

type MobileStoreNavigationProps = {
  activeSection: MobileStoreSection | null
  cartCount: number
  homeHref: string
  favoritesHref: string
  cartHref: string
  onAccountClick: () => void
  renderCatalogTrigger?: (trigger: ReactElement) => ReactNode
}

const MobileStoreNavigation = ({
  activeSection,
  cartCount,
  homeHref,
  favoritesHref,
  cartHref,
  onAccountClick,
  renderCatalogTrigger,
}: MobileStoreNavigationProps) => {
  const handleCurrentSection = (section: MobileStoreSection) => {
    if (activeSection === section) {
      const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" })
      return true
    }

    return false
  }

  const catalogTrigger = (
    <DockButton
      label="Каталог"
      icon={TextSearchIcon}
      active={activeSection === "catalog"}
      onClick={() => handleCurrentSection("catalog")}
    />
  )

  return (
    <nav
      aria-label="Основная навигация"
      data-slot="mobile-store-navigation"
      className="flex w-full items-stretch gap-0.5"
    >
      <DockLink href={homeHref} label="Главная" icon={HomeIcon} active={activeSection === "home"} onCurrent={() => handleCurrentSection("home")} />
      {renderCatalogTrigger ? renderCatalogTrigger(catalogTrigger) : catalogTrigger}
      <DockLink href={favoritesHref} label="Избранное" icon={HeartIcon} active={activeSection === "favorites"} onCurrent={() => handleCurrentSection("favorites")} />
      <DockLink href={cartHref} label="Корзина" icon={ShoppingBagIcon} active={activeSection === "cart"} count={cartCount} onCurrent={() => handleCurrentSection("cart")} />
      <DockButton
        label="Аккаунт"
        icon={UserRoundIcon}
        active={activeSection === "account"}
        onClick={onAccountClick}
      />
    </nav>
  )
}

type DockItemProps = {
  label: string
  icon: LucideIcon
  active: boolean
  count?: number
}

const dockItemLayoutClassName = "relative min-w-0 flex-1"

const DockContent = ({ label, icon: Icon, count }: Omit<DockItemProps, "active">) => (
  <>
    <span className="relative grid size-6 place-items-center" aria-hidden="true">
      <Icon strokeWidth={1.9} />
      {count ? (
        <Badge className="absolute -right-2 -top-2.5 z-10 size-5 min-h-5 min-w-5 border-2 border-white bg-primary px-0 py-0 text-[10px] font-[650] leading-none text-primary-foreground shadow-sm">
          {count > 99 ? "99+" : count}
        </Badge>
      ) : null}
    </span>
    <span className="truncate">{label}</span>
  </>
)

const DockLink = ({ href, label, icon, active, count, onCurrent }: DockItemProps & { href: string; onCurrent: () => boolean }) => (
  <Link
    to={href}
    className={cn(buttonVariants({ variant: "navigation", size: "dock" }), dockItemLayoutClassName)}
    data-active={active}
    aria-current={active ? "page" : undefined}
    aria-label={count ? `${label}: ${count}` : label}
    onClick={(event) => {
      if (onCurrent()) event.preventDefault()
    }}
  >
    <DockContent label={label} icon={icon} count={count} />
  </Link>
)

const DockButton = forwardRef<HTMLButtonElement, DockItemProps & { onClick: () => void }>(
  ({ label, icon, active, count, onClick }, ref) => (
    <Button
      ref={ref}
      type="button"
      variant="navigation"
      size="dock"
      data-motion="static"
      className={dockItemLayoutClassName}
      data-active={active}
      aria-current={active ? "page" : undefined}
      aria-label={count ? `${label}: ${count}` : label}
      onClick={onClick}
    >
      <DockContent label={label} icon={icon} count={count} />
    </Button>
  )
)

DockButton.displayName = "DockButton"

export default MobileStoreNavigation
